/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import StoreDataContext from "src/app/context/store-data";
import isFeatureEnabled from "src/app/utilities/isFeatureEnabled";
import Main from "src/app/components/Main/Main";
import Aside from "src/app/components/Aside/Aside";
import Card from "src/app/components/Card/Card";
import ViewRelatedQuestions from "src/app/components/ViewRelatedQuestions/ViewRelatedQuestions";
import noticeError from "src/app/utilities/noticeError";
import getQueryParamValue from "src/utilities/getQueryParamValue";

import { SierraChat } from "src/app/components/SierraChat/SierraChat";
import SierraChatButton from "src/app/components/SierraChatButton/SierraChatButton";
import { useExperiment } from "src/app/hooks/useExperiment";
import { EXPERIMENTS } from "src/app/utilities/experiments";
import { useNuanceBucket } from "../../hooks/useNuanceBucket";
import HeroSection from "./HeroSection";
import useOutOfHours from "../../hooks/useOutOfHours";
import NuanceChat from "./NuanceChat";
import { isWebView } from "../../../utilities/isWebView";

export default function GetInTouch({ content, storeData }) {
  useNuanceBucket();
  const [hasMounted, setHasMounted] = useState(false);

  const [showSierraChat, setShowSierraChat] = useState(() => {
    return getQueryParamValue("showChatWidget") === "true" || false;
  });

  const [sierraChatBotEnabled, setSierraChatBotEnabled] = useState(false);
  const [sierraLoadError, setSierraLoadError] = useState(null);
  const [chatBotEnabled, setChatBotEnabled] = useState(false);
  const [chatBotStatus, setChatBotStatus] = useState("loading");

  const { language, storeCode, countryCode } = useContext(StoreDataContext);
  const { outOfHours } = useOutOfHours({ storeCode, countryCode });

  const bucket = useExperiment(EXPERIMENTS.SIERRA_CHAT.KEY);

  const loadNuanceChat = bucket === EXPERIMENTS.SIERRA_CHAT.VARIATIONS.CONTROL;
  const sierraChatEnabled =
    bucket === EXPERIMENTS.SIERRA_CHAT.VARIATIONS.VARIANT;

  const body = content?.main?.body || {};
  const aside = content?.aside;
  const popularArticles = aside?.["popular-articles"]?.elements[0];
  const chatNowChannel = body.elements[0]?.["chat-channel"];
  const channels = body.elements[0]?.channels;

  const showHero = chatBotStatus !== "chatactive";
  const webView = isWebView();

  const showSierra = !outOfHours && (sierraChatBotEnabled || webView);
  const showNuance = !showSierra || (loadNuanceChat && !webView);

  useEffect(() => {
    async function load() {
      try {
        const chatBotEnabled = await isFeatureEnabled("cchp-chat-bot");
        setChatBotEnabled(chatBotEnabled);

        const status = !chatBotEnabled || outOfHours ? "disabled" : "loading";
        setChatBotStatus(status);
      } catch (error) {
        noticeError(error);
      }
    }

    setSierraChatBotEnabled(sierraChatEnabled);
    setHasMounted(true);
    load();
  }, [bucket]);

  if (!hasMounted) return null;

  return (
    <>
      <Main>
        {showSierraChat ? (
          <SierraChat
            setShow={setShowSierraChat}
            setLoadError={setSierraLoadError}
          />
        ) : (
          <Card
            type="getInTouch"
            ariaLive="polite"
            ariaRelevant="additions text"
          >
            {showHero && <HeroSection body={content?.main?.body} />}
            {showSierra && (
              <SierraChatButton
                sierraLoadError={sierraLoadError}
                isWebView={webView}
              />
            )}
            {showNuance && (
              <NuanceChat
                chatBotStatus={chatBotStatus}
                chatBotEnabled={chatBotEnabled}
                channels={channels}
                chatNowChannel={chatNowChannel}
                countryCode={countryCode}
                isFeatureEnabled={isFeatureEnabled}
                outOfHours={outOfHours}
                language={language}
                loadNuanceChat={loadNuanceChat}
                setChatBotStatus={setChatBotStatus}
                storeCode={storeCode}
                storeData={storeData}
              />
            )}
          </Card>
        )}
      </Main>
      <Aside>
        <Card type="aside">
          <ViewRelatedQuestions
            data={popularArticles}
            showButton={false}
            visibleItems={6}
          />
        </Card>
      </Aside>
    </>
  );
}

GetInTouch.propTypes = {
  content: PropTypes.object,
  storeData: PropTypes.object,
};
